import React, { useState, useEffect } from 'react';
import { AddressResult } from '../types';
import { 
    getRecentSearches, 
    removeRecentSearch, 
    clearRecentSearches, 
    formatSearchTime,
    isSessionStorageAvailable 
} from '../utils/recentSearches';
import './RecentSearches.css';

interface RecentSearchesProps {
    onAddressSelect: (address: AddressResult) => void;
    isVisible: boolean;
}

const RecentSearches: React.FC<RecentSearchesProps> = ({ onAddressSelect, isVisible }) => {
    const [recentSearches, setRecentSearches] = useState(getRecentSearches());
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        if (isVisible) {
            setRecentSearches(getRecentSearches());
        }
    }, [isVisible]);

    const handleAddressSelect = (address: AddressResult) => {
        onAddressSelect(address);
    };

    const handleRemoveSearch = (searchId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        removeRecentSearch(searchId);
        setRecentSearches(getRecentSearches());
    };

    const handleClearAll = () => {
        clearRecentSearches();
        setRecentSearches([]);
    };

    const handleToggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    if (!isSessionStorageAvailable()) {
        return null;
    }

    if (recentSearches.length === 0) {
        return null;
    }

    const displaySearches = isExpanded ? recentSearches : recentSearches.slice(0, 3);

    return (
        <div className="recent-searches">
            <div className="recent-searches-header">
                <div className="recent-searches-title">
                    <span className="recent-searches-icon">🕒</span>
                    <span className="recent-searches-text">Recent Searches</span>
                    <span className="recent-searches-count">({recentSearches.length})</span>
                </div>
                <div className="recent-searches-actions">
                    {recentSearches.length > 3 && (
                        <button
                            className="toggle-button"
                            onClick={handleToggleExpanded}
                            aria-label={isExpanded ? 'Show less' : 'Show more'}
                        >
                            {isExpanded ? 'Show Less' : 'Show More'}
                        </button>
                    )}
                    <button
                        className="clear-button"
                        onClick={handleClearAll}
                        aria-label="Clear all recent searches"
                        title="Clear all recent searches"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                            <path d="M10 11v6M14 11v6"/>
                        </svg>
                    </button>
                </div>
            </div>

            <div className="recent-searches-list">
                {displaySearches.map((search) => (
                    <div
                        key={search.id}
                        className="recent-search-item"
                        onClick={() => handleAddressSelect(search.address)}
                    >
                        <div className="recent-search-content">
                            <div className="recent-search-address">
                                <span className="recent-search-name">{search.address.name}</span>
                                <span className="recent-search-district">{search.address.district}</span>
                            </div>
                            <div className="recent-search-meta">
                                <span className="recent-search-time">
                                    {formatSearchTime(search.timestamp)}
                                </span>
                                {search.searchCount > 1 && (
                                    <span className="recent-search-count">
                                        {search.searchCount} times
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            className="remove-search-button"
                            onClick={(e) => handleRemoveSearch(search.id, e)}
                            aria-label={`Remove ${search.address.name} from recent searches`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default RecentSearches;
